import { 
  List, ListItemButton, ListItemText, ListItem, IconButton, 
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, 
  Button, TextField, CircularProgress, Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import FetchService from "../../../services/FetchService";
import endpoints from "../../../environment/endpoints";
import { svr_listElement } from "../../../types";
import style from "./list.module.css";

export default function MashListList() {
  const fetchService = new FetchService();
  const [mashLists, setMashLists] = useState<svr_listElement[]>([]);
  const [fetchError, setFetchError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Rename Dialog State
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [listToRename, setListToRename] = useState<svr_listElement | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [renameError, setRenameError] = useState("");

  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState<svr_listElement | null>(null);
  const [deleteError, setDeleteError] = useState("");

  const fetchLists = useCallback(() => {
    setIsLoading(true);
    fetchService
      .get(endpoints.getMyMashLists)
      .then((r) => {
        if (r.success) {
          setMashLists(r.data);
        } else {
          setFetchError(r.data?.message || r.errors || "Failed to fetch lists.");
        }
      })
      .catch((reason) => setFetchError(String(reason)))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const handleOpenRename = (e: React.MouseEvent, ml: svr_listElement) => {
    e.stopPropagation();
    e.preventDefault();
    setListToRename(ml);
    setNewTitle(ml.name);
    setRenameDialogOpen(true);
  };

  const submitRename = async () => {
    if (!listToRename || !newTitle.trim()) return;
    setRenameError("");
    const res = await fetchService.post(endpoints.renameMashList, { title: newTitle }, listToRename.id);
    if (res.success) {
      setRenameDialogOpen(false);
      fetchLists();
    } else {
      setRenameError(res.data?.message || res.errors || "Failed to rename.");
    }
  };

  const handleOpenDelete = (e: React.MouseEvent, ml: svr_listElement) => {
    e.stopPropagation();
    e.preventDefault();
    setListToDelete(ml);
    setDeleteDialogOpen(true);
  };

  const submitDelete = async () => {
    if (!listToDelete) return;
    setDeleteError("");
    const res = await fetchService.delete(endpoints.deleteMashList, listToDelete.id);
    if (res.success) {
      setDeleteDialogOpen(false);
      fetchLists();
    } else {
      setDeleteError(res.data?.message || res.errors || "Failed to delete.");
    }
  };

  if (isLoading) return <CircularProgress style={{ display: 'block', margin: '40px auto' }} />;

  if (fetchError) {
    return <Alert severity="error" style={{ margin: '20px' }}>{fetchError}</Alert>;
  }

  return (
    <div className={style.container}>
      <h1>My Lists</h1>
      
      {mashLists.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <p>You haven't created any mashlists yet.</p>
          <Button 
            variant="contained" 
            color="secondary" 
            component={Link}
            to="/mashlist/wizard"
            sx={{ mt: 2, px: 4, py: 1.5, borderRadius: '25px', fontWeight: 'bold' }}
          >
            Create Your First Mashlist
          </Button>
        </div>
      ) : (
        <div className={style.listRegion}>
          <List>
            {mashLists.map((ml) => (
              <ListItem
                key={ml.id}
                className={"mui-box-shadow " + style.listItemButton}
                disablePadding
                secondaryAction={
                  <>
                    <IconButton edge="end" aria-label="edit" onClick={(e) => handleOpenRename(e, ml)} sx={{ mr: 1 }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={(e) => handleOpenDelete(e, ml)}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemButton href={"/mashlist/" + ml.id + "/manage"}>
                  <ListItemText primary={ml.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </div>
      )}

      {/* RENAME DIALOG */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Rename Mash List</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a new title for "{listToRename?.name}".
          </DialogContentText>
          {renameError && <Alert severity="error" sx={{ mt: 2 }}>{renameError}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            label="New Title"
            type="text"
            fullWidth
            variant="outlined"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitRename} variant="contained" color="secondary">Save</Button>
        </DialogActions>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Mash List?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete "{listToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
